/**
 * SOAPPreview Component
 *
 * Displays pre-visit SOAP (Subjective, Objective, Assessment, Plan) data
 * from the DiagnosticReport in an accordion-style layout matching Figma design.
 */

import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/base/Accordion';
import { Card, CardContent } from '@/components/base/Card';
import { PatientReport, HeartbeatWave } from '@/assets/icons';
import { cn } from '@/lib/utils';
import type { VitalSigns } from '@/lib/types/consultation';
import { sanitizeClinicalHtml as sanitizeFhirHtml } from '@/lib/utils/clinicalText';

export interface SoapSection {
  title: string;
  content: string;
}

export interface SOAPData {
  /** Flat sections from the Composition (Chief Complaint, HPI, etc.) */
  sections?: SoapSection[];
}

export interface SOAPPreviewProps {
  soapData: SOAPData | null;
  isLoading?: boolean;
  className?: string;
  vitals?: VitalSigns | null;
}

export function SOAPPreview({ soapData, isLoading, className, vitals }: SOAPPreviewProps) {
  if (isLoading) {
    return (
      <Card
        padding="none"
        className={cn(
          'mb-4 rounded-[20px] border-[#fbfafd] bg-[rgba(253,252,250,0.5)] backdrop-blur-[15px]',
          className
        )}
      >
        <CardContent className="flex flex-col gap-4 p-5">
          <div className="animate-pulse">
            <div className="mb-2 h-6 w-3/4 rounded bg-gray-200"></div>
            <div className="h-4 w-full rounded bg-gray-200"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sections = soapData?.sections || [];
  const vitalsContent = formatVitalsContent(vitals);

  return (
    <Accordion type="multiple" className="w-full" defaultValue={['Vitals', 'Clinical Notes']}>
      {/* Vitals Section */}
      <SOAPSection
        title="Vitals"
        icon={<HeartbeatWave size={22} className="text-[#384881]" />}
        content={vitalsContent}
        headerClassName="font-hanken text-[20px] font-medium leading-6 tracking-normal text-[#384881]"
      />

      {/* Clinical Notes - All sections as bullet points */}
      <SOAPSectionWithSubsections
        title="Clinical Notes"
        icon={<PatientReport size={20} />}
        content={null}
        subsections={sections}
        headerClassName="font-hanken text-[20px] font-medium leading-6 tracking-normal text-[#384881]"
      />
    </Accordion>
  );
}

interface SOAPSectionProps {
  title: string;
  icon: React.ReactNode;
  content: string | null;
  headerClassName?: string;
}

function SOAPSection({ title, icon, content, headerClassName }: SOAPSectionProps) {
  const hasContent = content && content.trim().length > 0;
  const displayText = hasContent ? content : 'No data available';

  return (
    <AccordionItem
      value={title}
      className={cn(
        'rounded-xl transition-all',
        'data-[state=open]:glass-panel-blur data-[state=open]:mb-2'
      )}
    >
      <AccordionTrigger>
        <div
          className={cn(
            'flex items-center justify-center gap-2 text-left font-semibold text-deep-ocean',
            headerClassName
          )}
        >
          {icon}
          <span>{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <p
          className={cn(
            'whitespace-pre-wrap text-lg leading-8',
            hasContent ? 'text-foreground' : 'italic text-muted-foreground'
          )}
        >
          {displayText}
        </p>
      </AccordionContent>
    </AccordionItem>
  );
}

interface SOAPSectionWithSubsectionsProps {
  title: string;
  icon: React.ReactNode;
  content: string | null;
  subsections?: SoapSection[];
  headerClassName?: string;
}

function SOAPSectionWithSubsections({
  title,
  icon,
  content,
  subsections = [],
  headerClassName,
}: SOAPSectionWithSubsectionsProps) {
  const hasContent = content && content.trim().length > 0;
  const hasSubsections = subsections.length > 0;

  return (
    <AccordionItem
      value={title}
      className={cn(
        'rounded-xl transition-all',
        'data-[state=open]:glass-panel-blur data-[state=open]:mb-2'
      )}
    >
      <AccordionTrigger>
        <div
          className={cn(
            'flex items-center justify-center gap-2 text-left font-semibold text-deep-ocean',
            headerClassName
          )}
        >
          {icon}
          <span>{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4">
          {/* Render parent content if exists */}
          {hasContent && <p className="whitespace-pre-wrap text-sm text-foreground">{content}</p>}

          {/* Render nested subsections as bullet points */}
          {hasSubsections && (
            <ul className="list-disc space-y-4 pl-5 marker:text-deep-ocean">
              {subsections.map((subsection, index) => (
                <li key={index} className="space-y-2">
                  <span className="text-lg font-semibold leading-7 text-deep-ocean">
                    {subsection.title}
                  </span>
                  <div
                    className="pl-4 text-lg leading-8 text-foreground [&_li]:my-1 [&_p]:my-1 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: sanitizeFhirHtml(subsection.content) }}
                  />
                </li>
              ))}
            </ul>
          )}

          {/* Show fallback if no content */}
          {!hasContent && !hasSubsections && (
            <p className="text-sm italic text-muted-foreground">No data available</p>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function formatVitalsContent(vitals?: VitalSigns | null): string {
  if (!vitals) return '';

  const lines: string[] = [];
  if (vitals.bloodPressure) lines.push(`- B.P.: ${vitals.bloodPressure} mmHg`);
  if (vitals.heartRate !== undefined && !isNaN(vitals.heartRate)) {
    lines.push(`- Heart Rate: ${vitals.heartRate} bpm`);
  }
  if (vitals.temperature !== undefined && !isNaN(vitals.temperature)) {
    lines.push(`- Temperature: ${vitals.temperature} °F`);
  }
  if (vitals.respiratoryRate !== undefined && !isNaN(vitals.respiratoryRate)) {
    lines.push(`- Respiratory Rate: ${vitals.respiratoryRate} /min`);
  }
  if (vitals.oxygenSaturation !== undefined && !isNaN(vitals.oxygenSaturation)) {
    lines.push(`- SpO2: ${vitals.oxygenSaturation} %`);
  }
  if (vitals.bloodSugar !== undefined && !isNaN(vitals.bloodSugar)) {
    lines.push(`- Blood Sugar: ${vitals.bloodSugar} mg/dL`);
  }
  if (vitals.weight !== undefined && !isNaN(vitals.weight)) {
    lines.push(`- Weight: ${vitals.weight} kg`);
  }
  if (vitals.height !== undefined && !isNaN(vitals.height)) {
    lines.push(`- Height: ${vitals.height} cm`);
  }

  return lines.length > 0 ? lines.join('\n') : 'No vitals recorded';
}
